import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from "react";

import "./SanDialer.css";

const SAN_ORIGIN = "https://ccsslb.sansoftwares.com";
const SAN_URL = `${SAN_ORIGIN}/callerMini`;

/* =========================================================
   ICONS
========================================================= */

const Icon = ({ name, size = 18 }) => {
    const common = {
        width: size,
        height: size,
        viewBox: "0 0 24 24",
        fill: "none",
        stroke: "currentColor",
        strokeWidth: 2,
        strokeLinecap: "round",
        strokeLinejoin: "round",
    };

    const icons = {
        phone: (
            <svg {...common}>
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2A19.79 19.79 0 0 1 3.09 5.18 2 2 0 0 1 5.11 3h3a2 2 0 0 1 2 1.72c.12.9.33 1.78.62 2.63a2 2 0 0 1-.45 2.11L9 10.73a16 16 0 0 0 4.27 4.27l1.27-1.27a2 2 0 0 1 2.11-.45c.85.29 1.73.5 2.63.62A2 2 0 0 1 22 16.92z" />
            </svg>
        ),

        phoneOff: (
            <svg {...common}>
                <path d="M10.68 13.31a16 16 0 0 0 2.63 1.69l1.27-1.27a2 2 0 0 1 2.11-.45c.85.29 1.73.5 2.63.62A2 2 0 0 1 21 15.92v3a2 2 0 0 1-2.18 2A19.79 19.79 0 0 1 3.09 5.18 2 2 0 0 1 5 3h3a2 2 0 0 1 2 1.72c.12.9.33 1.78.62 2.63a2 2 0 0 1-.45 2.11L9.9 10.73" />
                <line x1="3" y1="3" x2="21" y2="21" />
            </svg>
        ),

        mic: (
            <svg {...common}>
                <rect x="9" y="2" width="6" height="12" rx="3" />
                <path d="M5 10a7 7 0 0 0 14 0" />
                <line x1="12" y1="19" x2="12" y2="22" />
                <line x1="8" y1="22" x2="16" y2="22" />
            </svg>
        ),

        micOff: (
            <svg {...common}>
                <line x1="1" y1="1" x2="23" y2="23" />
                <path d="M9 9v3a3 3 0 0 0 5.12 2.12" />
                <path d="M15 9V5a3 3 0 0 0-5.12-2.12" />
                <path d="M5 10a7 7 0 0 0 12 4.9" />
                <line x1="12" y1="19" x2="12" y2="22" />
                <line x1="8" y1="22" x2="16" y2="22" />
            </svg>
        ),

        pause: (
            <svg {...common}>
                <rect x="6" y="4" width="4" height="16" />
                <rect x="14" y="4" width="4" height="16" />
            </svg>
        ),

        play: (
            <svg {...common} fill="currentColor">
                <path d="M8 5v14l11-7z" stroke="none" />
            </svg>
        ),

        user: (
            <svg {...common}>
                <circle cx="12" cy="8" r="4" />
                <path d="M4 21a8 8 0 0 1 16 0" />
            </svg>
        ),

        users: (
            <svg {...common}>
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
        ),

        backspace: (
            <svg {...common}>
                <path d="M3 12l5-6h13v12H8z" />
                <line x1="12" y1="9" x2="17" y2="15" />
                <line x1="17" y1="9" x2="12" y2="15" />
            </svg>
        ),

        close: (
            <svg {...common}>
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
        ),

        minimize: (
            <svg {...common}>
                <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
        ),

        maximize: (
            <svg {...common}>
                <polyline points="15 3 21 3 21 9" />
                <polyline points="9 21 3 21 3 15" />
                <line x1="21" y1="3" x2="14" y2="10" />
                <line x1="3" y1="21" x2="10" y2="14" />
            </svg>
        ),

        phoneIncoming: (
            <svg {...common}>
                <polyline points="16 3 21 3 21 8" />
                <line x1="21" y1="3" x2="14" y2="10" />
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2A19.79 19.79 0 0 1 3.09 5.18 2 2 0 0 1 5.11 3h3a2 2 0 0 1 2 1.72" />
            </svg>
        ),

        phoneOutgoing: (
            <svg {...common}>
                <polyline points="14 3 21 3 21 10" />
                <line x1="21" y1="3" x2="13" y2="11" />
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2A19.79 19.79 0 0 1 3.09 5.18 2 2 0 0 1 5.11 3h3a2 2 0 0 1 2 1.72" />
            </svg>
        ),

        history: (
            <svg {...common}>
                <path d="M3 12a9 9 0 1 0 3-6.7" />
                <polyline points="3 4 3 10 9 10" />
                <line x1="12" y1="7" x2="12" y2="12" />
                <line x1="12" y1="12" x2="15" y2="14" />
            </svg>
        ),

        grid: (
            <svg {...common}>
                <rect x="3" y="3" width="7" height="7" />
                <rect x="14" y="3" width="7" height="7" />
                <rect x="3" y="14" width="7" height="7" />
                <rect x="14" y="14" width="7" height="7" />
            </svg>
        ),

        settings: (
            <svg {...common}>
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06-1.42 1.42-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21h-2v-.08a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06-1.42-1.42.06-.06A1.65 1.65 0 0 0 9.6 15a1.65 1.65 0 0 0-1.51-1H8v-2h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06 1.42-1.42.06.06A1.65 1.65 0 0 0 12.5 8a1.65 1.65 0 0 0 1-1.51V6h2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06 1.42 1.42-.06.06A1.65 1.65 0 0 0 19.4 10c.2.61.76 1 1.4 1H21v2h-.2a1.65 1.65 0 0 0-1.4 1z" />
            </svg>
        ),

        conference: (
            <svg {...common}>
                <circle cx="9" cy="7" r="4" />
                <path d="M2 21a7 7 0 0 1 14 0" />
                <circle cx="17" cy="8" r="3" />
                <path d="M16 14a6 6 0 0 1 6 6" />
            </svg>
        ),
    };

    return icons[name] || null;
};

/* =========================================================
   DIALER
========================================================= */

const SanDialer = forwardRef(
    (
        {
            position = "bottom-right",

            onIncoming,
            onOutgoing,
            onCallEnded,
            onDisposition,
            onReady,
            onCallHistory,

            customerResolver,
        },
        ref
    ) => {
        const iframeRef = useRef(null);

        /* -----------------------------------------------------
           UI STATE
        ----------------------------------------------------- */

        const [open, setOpen] = useState(false);
        const [minimized, setMinimized] = useState(false);

        const [activeTab, setActiveTab] = useState("dialer");

        const [number, setNumber] = useState("");

        const [status, setStatus] = useState("Idle");
        const [extension, setExtension] = useState("");

        const [callType, setCallType] = useState("");
        const [callState, setCallState] = useState("");

        const [currentPhone, setCurrentPhone] = useState("");

        const [isHold, setIsHold] = useState(false);
        const [isMuted, setIsMuted] = useState(false);

        const [manualMode, setManualMode] = useState(false);

        const [incomingCall, setIncomingCall] = useState(null);

        const [callStartedAt, setCallStartedAt] = useState(null);
        const [callDuration, setCallDuration] = useState(0);

        const [dispositions, setDispositions] = useState([]);

        const [selectedDisposition, setSelectedDisposition] =
            useState("");

        const [remarks, setRemarks] = useState("");

        const [conferenceOpen, setConferenceOpen] =
            useState(false);

        const [conferenceNumber, setConferenceNumber] =
            useState("");

        const [conferenceMembers, setConferenceMembers] =
            useState([]);

        const [callHistory, setCallHistory] = useState([]);

        /* -----------------------------------------------------
           CALL TIMER
        ----------------------------------------------------- */

        useEffect(() => {
            if (!callStartedAt) {
                return;
            }

            const timer = setInterval(() => {
                setCallDuration(
                    Math.floor(
                        (Date.now() - callStartedAt) / 1000
                    )
                );
            }, 1000);

            return () => clearInterval(timer);
        }, [callStartedAt]);

        const formattedDuration = useMemo(() => {
            const minutes = Math.floor(callDuration / 60);
            const seconds = callDuration % 60;

            return `${String(minutes).padStart(
                2,
                "0"
            )}:${String(seconds).padStart(2, "0")}`;
        }, [callDuration]);

        /* -----------------------------------------------------
           SEND TO SAN CHILD
        ----------------------------------------------------- */

        const sendToChild = useCallback(
            (type, payload = {}) => {
                const child = iframeRef.current?.contentWindow;

                if (!child) {
                    console.warn(
                        "SAN Dialer iframe is not ready"
                    );

                    return false;
                }

                child.postMessage(
                    {
                        type,
                        ...payload,
                    },
                    SAN_ORIGIN
                );

                return true;
            },
            []
        );

        /* -----------------------------------------------------
           PUBLIC API
        ----------------------------------------------------- */

        const dial = useCallback(
            (phone, uniqueId = "") => {
                if (!phone) {
                    return false;
                }

                const cleanPhone = String(phone).trim();

                setNumber(cleanPhone);
                setCurrentPhone(cleanPhone);

                setOpen(true);
                setMinimized(false);

                setCallType("Outgoing");
                setCallState("Dialing");
                setStatus("Dialing");

                sendToChild("dial", {
                    number: cleanPhone,
                    uniqueId,
                });

                return true;
            },
            [sendToChild]
        );

        const answer = useCallback(() => {
            sendToChild("IncAccept");

            setIncomingCall(null);
            setStatus("In Call");
            setCallState("Answer");

            setCallStartedAt(Date.now());
            setCallDuration(0);
        }, [sendToChild]);

        const hangup = useCallback(() => {
            sendToChild("Hangup");

            setCallState("Hangup");
            setStatus("Hangup");
        }, [sendToChild]);

        const hold = useCallback(() => {
            sendToChild("HoldCall");
            setIsHold(true);
        }, [sendToChild]);

        const unhold = useCallback(() => {
            sendToChild("UnholdCall");
            setIsHold(false);
        }, [sendToChild]);

        const toggleHold = useCallback(() => {
            if (isHold) {
                unhold();
            } else {
                hold();
            }
        }, [isHold, hold, unhold]);

        const mute = useCallback(() => {
            sendToChild("MuteCall");
            setIsMuted(true);
        }, [sendToChild]);

        const unmute = useCallback(() => {
            sendToChild("UnmuteCall");
            setIsMuted(false);
        }, [sendToChild]);

        const toggleMute = useCallback(() => {
            if (isMuted) {
                unmute();
            } else {
                mute();
            }
        }, [isMuted, mute, unmute]);

        const ready = useCallback(() => {
            sendToChild("ready");
        }, [sendToChild]);

        const manualOn = useCallback(() => {
            sendToChild("ManualOn");
            setManualMode(true);
        }, [sendToChild]);

        const manualOff = useCallback(() => {
            sendToChild("ManualOff");
            setManualMode(false);
        }, [sendToChild]);

        const toggleManual = useCallback(() => {
            if (manualMode) {
                manualOff();
            } else {
                manualOn();
            }
        }, [manualMode, manualOn, manualOff]);

        const conferenceToggle = useCallback(() => {
            sendToChild("ConfrenceToggle");

            setConferenceOpen(true);
        }, [sendToChild]);

        const conferenceAdd = useCallback(() => {
            const phone = conferenceNumber.trim();

            if (!phone) {
                return;
            }

            sendToChild("ConfrenceNumber", {
                phone,
            });

            setConferenceNumber("");
        }, [conferenceNumber, sendToChild]);

        const conferenceHoldToggle = useCallback(
            (phone) => {
                sendToChild("ConfHoldToggle", {
                    phone,
                });
            },
            [sendToChild]
        );

        const submitDisposition = useCallback(() => {
            if (!selectedDisposition) {
                return;
            }

            sendToChild("SubmitDisposition", {
                disposition: selectedDisposition,
                remark: remarks,
                phone_number: currentPhone,
            });

            const dispositionData = {
                disposition: selectedDisposition,
                remark: remarks,
                phone_number: currentPhone,
            };

            onDisposition?.(dispositionData);

            setSelectedDisposition("");
            setRemarks("");
        }, [
            selectedDisposition,
            remarks,
            currentPhone,
            sendToChild,
            onDisposition,
        ]);

        /* -----------------------------------------------------
           EXPOSE FUNCTIONS TO PARENT
        ----------------------------------------------------- */

        useImperativeHandle(
            ref,
            () => ({
                dial,
                answer,
                hangup,

                hold,
                unhold,
                toggleHold,

                mute,
                unmute,
                toggleMute,

                ready,

                manualOn,
                manualOff,
                toggleManual,

                conferenceToggle,
                conferenceAdd: (phone) => {
                    sendToChild("ConfrenceNumber", {
                        phone,
                    });
                },

                conferenceHoldToggle,

                submitDisposition,

                open: () => {
                    setOpen(true);
                    setMinimized(false);
                },

                close: () => {
                    setOpen(false);
                },

                minimize: () => {
                    setMinimized(true);
                },

                maximize: () => {
                    setMinimized(false);
                },

                toggle: () => {
                    setOpen((value) => !value);
                },

                getState: () => ({
                    status,
                    extension,
                    callType,
                    callState,
                    currentPhone,
                    isHold,
                    isMuted,
                    manualMode,
                    incomingCall,
                    callDuration,
                    dispositions,
                    conferenceMembers,
                }),
            }),
            [
                dial,
                answer,
                hangup,
                hold,
                unhold,
                toggleHold,
                mute,
                unmute,
                toggleMute,
                ready,
                manualOn,
                manualOff,
                toggleManual,
                conferenceToggle,
                conferenceHoldToggle,
                submitDisposition,
                sendToChild,
                status,
                extension,
                callType,
                callState,
                currentPhone,
                isHold,
                isMuted,
                manualMode,
                incomingCall,
                callDuration,
                dispositions,
                conferenceMembers,
            ]
        );

        /* -----------------------------------------------------
           RECEIVE EVENTS FROM SAN CHILD
        ----------------------------------------------------- */

        useEffect(() => {
            const handleMessage = async (event) => {
                /*
                 * Only accept events from SAN.
                 */
                if (event.origin !== SAN_ORIGIN) {
                    return;
                }

                if (
                    iframeRef.current &&
                    event.source !==
                    iframeRef.current.contentWindow
                ) {
                    return;
                }

                const message = event.data || {};
                const type = message.type;
                const data = message.payload;

                if (!type) {
                    return;
                }

                console.log(
                    "[SAN CTI EVENT]",
                    type,
                    message
                );

                /* ================================================
                   READY
                ================================================ */

                if (type === "SANAppReadyEvent") {
                    setStatus("Idle");

                    if (data?.exten) {
                        setExtension(data.exten);
                    }

                    onReady?.(data);

                    return;
                }

                /* ================================================
                   INIT
                ================================================ */

                if (type === "SANAppInitEvent") {

                    if (data?.login_extension_no) {
                        setExtension(
                            data.login_extension_no
                        );
                    }

                    switch (String(data?.status)) {
                        case "3":
                            setStatus("Idle");
                            break;

                        case "10":
                            setStatus("Manual Dial On");
                            setManualMode(true);
                            break;

                        case "11":
                            setStatus("Manual Dial Off");
                            setManualMode(false);
                            break;

                        case "4":
                            setStatus("On Break");
                            break;

                        case "1":
                            setStatus("Logged In");
                            break;

                        case "5":
                            setStatus("Disposition");
                            break;

                        default:
                            break;
                    }

                    return;
                }

                /* ================================================
                   OUTGOING
                ================================================ */

                if (type === "SANAppOutgoingEvent") {
                    const phone =
                        data?.caller_id ||
                        data?.phone_number ||
                        currentPhone;

                    if (phone) {
                        setCurrentPhone(phone);
                    }

                    setCallType(
                        data?.call_type || "Outgoing"
                    );

                    setCallState(
                        data?.exten_status || ""
                    );

                    if (data?.exten) {
                        setExtension(data.exten);
                    }

                    if (
                        data?.exten_status ===
                        "Dialing"
                    ) {
                        setStatus("Dialing");

                        setOpen(true);
                        setMinimized(false);
                    }

                    if (
                        data?.exten_status ===
                        "Answer"
                    ) {
                        setStatus("In Call");

                        setCallStartedAt(Date.now());
                        setCallDuration(0);

                        setOpen(true);
                        setMinimized(false);
                    }

                    if (
                        data?.exten_status ===
                        "Hangup"
                    ) {
                        setStatus("Hangup");

                        setCallStartedAt(null);

                        setIsHold(false);
                        setIsMuted(false);

                        const historyItem = {
                            id:
                                data?.ID ||
                                `${Date.now()}`,

                            phone,

                            type: "Outgoing",

                            status: "Hangup",

                            duration:
                                data?.durn ||
                                callDuration,

                            billsec:
                                data?.billsec,

                            recording:
                                data?.rec_file ||
                                data?.RecFile,

                            disposition:
                                data?.disposition,

                            cause:
                                data?.cause_txt ||
                                data?.cause,

                            timestamp:
                                new Date().toISOString(),

                            raw: data,
                        };

                        setCallHistory(
                            (previous) => [
                                historyItem,
                                ...previous,
                            ].slice(0, 20)
                        );

                        onCallEnded?.(historyItem);
                        onCallHistory?.(historyItem);
                    }

                    onOutgoing?.(data);

                    return;
                }

                /* ================================================
                   INCOMING
                ================================================ */

                if (type === "SANAppIncomingEvent") {
                    const phone =
                        data?.caller_id ||
                        data?.phone_number ||
                        "";

                    if (phone) {
                        setCurrentPhone(phone);
                    }

                    setCallType("Incoming");

                    setCallState(
                        data?.exten_status || ""
                    );

                    /*
                     * Incoming ringing
                     */
                    if (
                        data?.exten_status ===
                        "Ringing"
                    ) {
                        setStatus("Incoming Call");

                        setIncomingCall(data);

                        setOpen(true);
                        setMinimized(false);

                        /*
                         * Optional CRM customer lookup.
                         */
                        if (
                            customerResolver &&
                            phone
                        ) {
                            try {
                                const customer =
                                    await customerResolver(
                                        phone
                                    );

                                setIncomingCall(
                                    (previous) => ({
                                        ...previous,
                                        customer,
                                    })
                                );

                                onIncoming?.({
                                    ...data,
                                    customer,
                                });
                            } catch (error) {
                                console.error(
                                    "Customer lookup failed:",
                                    error
                                );

                                onIncoming?.(data);
                            }
                        } else {
                            onIncoming?.(data);
                        }
                    }

                    /*
                     * Answered
                     */
                    if (
                        data?.exten_status ===
                        "Answer"
                    ) {
                        setStatus("In Call");

                        setIncomingCall(null);

                        setCallStartedAt(Date.now());
                        setCallDuration(0);
                    }

                    /*
                     * Hangup
                     */
                    if (
                        data?.exten_status ===
                        "Hangup"
                    ) {
                        setStatus("Hangup");

                        setIncomingCall(null);
                        setCallStartedAt(null);

                        setIsHold(false);
                        setIsMuted(false);
                    }

                    return;
                }

                /* ================================================
                   HOLD
                ================================================ */

                if (type === "SANAppHoldEvent") {
                    const holdState =
                        String(data?.set) === "1";

                    setIsHold(holdState);

                    setStatus(
                        holdState
                            ? "On Hold"
                            : "In Call"
                    );

                    return;
                }

                /* ================================================
                   MANUAL ON/OFF
                ================================================ */

                if (
                    type ===
                    "SANAppManualOnOffEvent"
                ) {
                    const enabled =
                        String(data?.state) ===
                        "10";

                    setManualMode(enabled);

                    setStatus(
                        enabled
                            ? "Manual Dial On"
                            : "Manual Dial Off"
                    );

                    return;
                }

                /* ================================================
                   BREAK
                ================================================ */

                if (
                    type ===
                    "SANAppBreakEvent"
                ) {
                    if (data?.state === 4) {
                        setStatus(
                            `On Break${data?.data?.break_name
                                ? ` - ${data.data.break_name}`
                                : ""
                            }`
                        );
                    }

                    return;
                }

                /* ================================================
                   LOGOUT EVENT
        
                   We don't provide logout UI, but we still listen
                   because CRM can log the event.
                ================================================ */

                if (
                    type ===
                    "SANAppLogoutEvent"
                ) {
                    setStatus("Logout");
                    return;
                }

                /* ================================================
                   DISPOSITION LIST
                ================================================ */

                if (
                    type ===
                    "SEND_DISPOSITION"
                ) {
                    const list = Array.isArray(data)
                        ? data
                        : [];

                    setDispositions(list);

                    return;
                }

                /* ================================================
                   SAVE PAGE / DISPOSITION COMPLETE
                ================================================ */

                if (
                    type ===
                    "SANAppSavePageEvent"
                ) {
                    setStatus("Idle");

                    setCallState("");

                    setIsHold(false);
                    setIsMuted(false);

                    setCallStartedAt(null);

                    return;
                }

                /* ================================================
                   CONFERENCE JOIN
                ================================================ */

                if (
                    type ===
                    "SANAppConfEventjoin"
                ) {
                    const members =
                        data?.conf_memeber ||
                        {};

                    const list = Object.keys(
                        members
                    )
                        .map((key) => members[key])
                        .filter(Boolean);

                    setConferenceMembers(list);

                    setConferenceOpen(true);

                    return;
                }

                /* ================================================
                   CONFERENCE DIALING
                ================================================ */

                if (
                    type ===
                    "conferenceDialing"
                ) {
                    const members =
                        data?.conference_dialing_members ||
                        {};

                    const list = Object.keys(
                        members
                    )
                        .map((key) => members[key])
                        .filter(Boolean);

                    setConferenceMembers(list);

                    setConferenceOpen(true);

                    return;
                }

                /* ================================================
                   CONFERENCE LEAVE
                ================================================ */

                if (
                    type ===
                    "SANAppConfEventLeave"
                ) {
                    const members =
                        data?.conf_memeber ||
                        {};

                    const list = Object.keys(
                        members
                    )
                        .map((key) => members[key])
                        .filter(Boolean);

                    setConferenceMembers(list);

                    return;
                }
            };

            window.addEventListener(
                "message",
                handleMessage
            );

            return () => {
                window.removeEventListener(
                    "message",
                    handleMessage
                );
            };
        }, [
            currentPhone,
            callDuration,
            customerResolver,
            onIncoming,
            onOutgoing,
            onCallEnded,
            onCallHistory,
            onReady,
        ]);

        /* -----------------------------------------------------
           KEYPAD
        ----------------------------------------------------- */

        const keypad = [
            {
                value: "1",
                letters: "",
            },
            {
                value: "2",
                letters: "ABC",
            },
            {
                value: "3",
                letters: "DEF",
            },
            {
                value: "4",
                letters: "GHI",
            },
            {
                value: "5",
                letters: "JKL",
            },
            {
                value: "6",
                letters: "MNO",
            },
            {
                value: "7",
                letters: "PQRS",
            },
            {
                value: "8",
                letters: "TUV",
            },
            {
                value: "9",
                letters: "WXYZ",
            },
            {
                value: "*",
                letters: "",
            },
            {
                value: "0",
                letters: "+",
            },
            {
                value: "#",
                letters: "",
            },
        ];

        const pressKey = (key) => {
            setNumber(
                (previous) =>
                    previous + key
            );
        };

        const backspace = () => {
            setNumber(
                (previous) =>
                    previous.slice(0, -1)
            );
        };

        const clearNumber = () => {
            setNumber("");
        };

        const dialCurrentNumber = () => {
            if (!number.trim()) {
                return;
            }

            dial(number);
        };

        /* -----------------------------------------------------
           POSITION
        ----------------------------------------------------- */

        const wrapperClass = [
            "san-dialer",
            `san-dialer-${position}`,
            open ? "is-open" : "is-closed",
            minimized ? "is-minimized" : "",
        ].join(" ");

        /* -----------------------------------------------------
           FLOATING BUTTON
        ----------------------------------------------------- */

        // if (!open) {
        //     return (
        //         <>
        //             <button
        //                 className={`san-dialer-float ${position}`}
        //                 onClick={() => {
        //                     setOpen(true);
        //                     setMinimized(false);
        //                 }}
        //                 title="Open Dialer"
        //             >
        //                 <Icon
        //                     name="phone"
        //                     size={25}
        //                 />

        //                 {incomingCall && (
        //                     <span className="san-dialer-float-badge">
        //                         1
        //                     </span>
        //                 )}
        //             </button>

        //             <iframe
        //                 ref={iframeRef}
        //                 title="SAN CTI"
        //                 src={SAN_URL}
        //                 className="san-dialer-hidden-iframe"
        //                 allow="microphone; camera"
        //             />
        //         </>
        //     );
        // }

        /* -----------------------------------------------------
           MAIN UI
        ----------------------------------------------------- */

        return (
            <>
                <div className={wrapperClass}>
                    {/* ================================================
              HEADER
          ================================================ */}

                    <div className="san-dialer-header">
                        <div className="san-dialer-agent">
                            <div className="san-dialer-agent-icon">
                                <Icon
                                    name="phone"
                                    size={17}
                                />
                            </div>

                            <div>
                                <div className="san-dialer-title">
                                    Dialer
                                </div>

                                <div className="san-dialer-status">
                                    <span
                                        className={`san-status-dot ${status === "In Call"
                                            ? "active"
                                            : ""
                                            }`}
                                    />

                                    {status}

                                    {extension
                                        ? ` • Ext. ${extension}`
                                        : ""}
                                </div>
                            </div>
                        </div>

                        <div className="san-dialer-header-actions">
                            <button
                                onClick={() =>
                                    setMinimized(
                                        (value) => !value
                                    )
                                }
                                title={
                                    minimized
                                        ? "Maximize"
                                        : "Minimize"
                                }
                            >
                                <Icon
                                    name={
                                        minimized
                                            ? "maximize"
                                            : "minimize"
                                    }
                                    size={17}
                                />
                            </button>

                            <button
                                onClick={() =>
                                    setOpen(false)
                                }
                                title="Close"
                            >
                                <Icon
                                    name="close"
                                    size={17}
                                />
                            </button>
                        </div>
                    </div>

                    {/* ================================================
              MINIMIZED
          ================================================ */}

                    {!minimized && (
                        <>
                            {/* ============================================
                  INCOMING CALL
              ============================================ */}

                            {incomingCall && (
                                <div className="san-incoming-card">
                                    <div className="san-incoming-icon">
                                        <Icon
                                            name="phoneIncoming"
                                            size={24}
                                        />
                                    </div>

                                    <div className="san-incoming-content">
                                        <div className="san-incoming-label">
                                            Incoming Call
                                        </div>

                                        <div className="san-incoming-number">
                                            {incomingCall.customer
                                                ?.name ||
                                                incomingCall.caller_id ||
                                                "Unknown"}
                                        </div>

                                        {incomingCall.customer
                                            ?.name && (
                                                <div className="san-incoming-phone">
                                                    {
                                                        incomingCall.caller_id
                                                    }
                                                </div>
                                            )}
                                    </div>

                                    <div className="san-incoming-actions">
                                        <button
                                            className="san-call-answer"
                                            onClick={answer}
                                        >
                                            <Icon
                                                name="phone"
                                                size={19}
                                            />
                                        </button>

                                        <button
                                            className="san-call-reject"
                                            onClick={hangup}
                                        >
                                            <Icon
                                                name="phoneOff"
                                                size={19}
                                            />
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* ============================================
                  ACTIVE CALL
              ============================================ */}

                            {(callState ===
                                "Answer" ||
                                status ===
                                "In Call" ||
                                status ===
                                "On Hold") && (
                                    <div className="san-active-call">
                                        <div className="san-call-avatar">
                                            <Icon
                                                name="user"
                                                size={23}
                                            />
                                        </div>

                                        <div className="san-call-details">
                                            <div className="san-call-number">
                                                {currentPhone ||
                                                    "Unknown"}
                                            </div>

                                            <div className="san-call-meta">
                                                {callType ||
                                                    "Call"}{" "}
                                                •{" "}
                                                {isHold
                                                    ? "On Hold"
                                                    : "Connected"}
                                            </div>
                                        </div>

                                        <div className="san-call-timer">
                                            {formattedDuration}
                                        </div>
                                    </div>
                                )}

                            {/* ============================================
                  TABS
              ============================================ */}

                            <div className="san-dialer-tabs">
                                <button
                                    className={
                                        activeTab ===
                                            "dialer"
                                            ? "active"
                                            : ""
                                    }
                                    onClick={() =>
                                        setActiveTab(
                                            "dialer"
                                        )
                                    }
                                >
                                    <Icon
                                        name="grid"
                                        size={15}
                                    />
                                    Dialer
                                </button>

                                <button
                                    className={
                                        activeTab ===
                                            "history"
                                            ? "active"
                                            : ""
                                    }
                                    onClick={() =>
                                        setActiveTab(
                                            "history"
                                        )
                                    }
                                >
                                    <Icon
                                        name="history"
                                        size={15}
                                    />
                                    History
                                </button>
                            </div>

                            {/* ============================================
                  DIALER TAB
              ============================================ */}

                            {activeTab ===
                                "dialer" && (
                                    <div className="san-dialer-body">
                                        {/* NUMBER DISPLAY */}

                                        <div className="san-number-container">
                                            <input
                                                value={number}
                                                onChange={(event) =>
                                                    setNumber(
                                                        event.target.value
                                                    )
                                                }
                                                placeholder="Enter phone number"
                                                className="san-number-input"
                                            />

                                            {number && (
                                                <button
                                                    className="san-clear-number"
                                                    onClick={
                                                        clearNumber
                                                    }
                                                >
                                                    <Icon
                                                        name="close"
                                                        size={15}
                                                    />
                                                </button>
                                            )}

                                            <button
                                                className="san-backspace"
                                                onClick={
                                                    backspace
                                                }
                                            >
                                                <Icon
                                                    name="backspace"
                                                    size={19}
                                                />
                                            </button>
                                        </div>

                                        {/* KEYPAD */}

                                        <div className="san-keypad">
                                            {keypad.map(
                                                (key) => (
                                                    <button
                                                        key={
                                                            key.value
                                                        }
                                                        onClick={() =>
                                                            pressKey(
                                                                key.value
                                                            )
                                                        }
                                                    >
                                                        <span>
                                                            {
                                                                key.value
                                                            }
                                                        </span>

                                                        {key.letters && (
                                                            <small>
                                                                {
                                                                    key.letters
                                                                }
                                                            </small>
                                                        )}
                                                    </button>
                                                )
                                            )}
                                        </div>

                                        {/* CALL BUTTON */}

                                        <button
                                            className="san-main-call-button"
                                            onClick={
                                                dialCurrentNumber
                                            }
                                            disabled={
                                                !number.trim()
                                            }
                                        >
                                            <Icon
                                                name="phone"
                                                size={21}
                                            />
                                            Call
                                        </button>

                                        {/* ========================================
                      ACTIVE CALL CONTROLS
                  ======================================== */}

                                        {(callState ===
                                            "Answer" ||
                                            status ===
                                            "In Call" ||
                                            status ===
                                            "On Hold") && (
                                                <div className="san-call-controls">
                                                    <button
                                                        className={
                                                            isHold
                                                                ? "active"
                                                                : ""
                                                        }
                                                        onClick={
                                                            toggleHold
                                                        }
                                                    >
                                                        <Icon
                                                            name={
                                                                isHold
                                                                    ? "play"
                                                                    : "pause"
                                                            }
                                                            size={18}
                                                        />

                                                        <span>
                                                            {isHold
                                                                ? "Unhold"
                                                                : "Hold"}
                                                        </span>
                                                    </button>

                                                    <button
                                                        className={
                                                            isMuted
                                                                ? "active"
                                                                : ""
                                                        }
                                                        onClick={
                                                            toggleMute
                                                        }
                                                    >
                                                        <Icon
                                                            name={
                                                                isMuted
                                                                    ? "micOff"
                                                                    : "mic"
                                                            }
                                                            size={18}
                                                        />

                                                        <span>
                                                            {isMuted
                                                                ? "Unmute"
                                                                : "Mute"}
                                                        </span>
                                                    </button>

                                                    <button
                                                        onClick={
                                                            conferenceToggle
                                                        }
                                                    >
                                                        <Icon
                                                            name="conference"
                                                            size={18}
                                                        />

                                                        <span>
                                                            Conference
                                                        </span>
                                                    </button>

                                                    <button
                                                        className="danger"
                                                        onClick={
                                                            hangup
                                                        }
                                                    >
                                                        <Icon
                                                            name="phoneOff"
                                                            size={18}
                                                        />

                                                        <span>
                                                            Hangup
                                                        </span>
                                                    </button>
                                                </div>
                                            )}

                                        {/* ========================================
                      MANUAL MODE
                  ======================================== */}

                                        <div className="san-manual-card">
                                            <div>
                                                <div className="san-manual-title">
                                                    Manual Dial
                                                </div>

                                                <div className="san-manual-description">
                                                    Allow manual dialing
                                                </div>
                                            </div>

                                            <button
                                                className={`san-switch ${manualMode
                                                    ? "on"
                                                    : ""
                                                    }`}
                                                onClick={
                                                    toggleManual
                                                }
                                            >
                                                <span />
                                            </button>
                                        </div>

                                        {/* ========================================
                      CONFERENCE
                  ======================================== */}

                                        {conferenceOpen && (
                                            <div className="san-conference-card">
                                                <div className="san-section-title">
                                                    <span>
                                                        Conference
                                                    </span>

                                                    <button
                                                        onClick={() =>
                                                            setConferenceOpen(
                                                                false
                                                            )
                                                        }
                                                    >
                                                        <Icon
                                                            name="close"
                                                            size={15}
                                                        />
                                                    </button>
                                                </div>

                                                <div className="san-conference-add">
                                                    <input
                                                        value={
                                                            conferenceNumber
                                                        }
                                                        onChange={(
                                                            event
                                                        ) =>
                                                            setConferenceNumber(
                                                                event
                                                                    .target
                                                                    .value
                                                            )
                                                        }
                                                        placeholder="Phone number"
                                                    />

                                                    <button
                                                        onClick={
                                                            conferenceAdd
                                                        }
                                                    >
                                                        Add
                                                    </button>
                                                </div>

                                                <div className="san-conference-members">
                                                    {conferenceMembers.length ===
                                                        0 && (
                                                            <div className="san-empty">
                                                                No conference
                                                                members
                                                            </div>
                                                        )}

                                                    {conferenceMembers.map(
                                                        (
                                                            member,
                                                            index
                                                        ) => {
                                                            const phone =
                                                                member.conf_member ||
                                                                member.caller_id ||
                                                                member.conf_exten;

                                                            return (
                                                                <div
                                                                    className="san-conference-member"
                                                                    key={`${phone}-${index}`}
                                                                >
                                                                    <div>
                                                                        <Icon
                                                                            name="user"
                                                                            size={15}
                                                                        />

                                                                        <span>
                                                                            {
                                                                                phone
                                                                            }
                                                                        </span>
                                                                    </div>

                                                                    <button
                                                                        onClick={() =>
                                                                            conferenceHoldToggle(
                                                                                phone
                                                                            )
                                                                        }
                                                                    >
                                                                        <Icon
                                                                            name="pause"
                                                                            size={15}
                                                                        />
                                                                    </button>
                                                                </div>
                                                            );
                                                        }
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {/* ========================================
                      DISPOSITION
                  ======================================== */}

                                        {status ===
                                            "Hangup" &&
                                            dispositions.length >
                                            0 && (
                                                <div className="san-disposition-card">
                                                    <div className="san-section-title">
                                                        Call Disposition
                                                    </div>

                                                    <select
                                                        value={
                                                            selectedDisposition
                                                        }
                                                        onChange={(
                                                            event
                                                        ) =>
                                                            setSelectedDisposition(
                                                                event
                                                                    .target
                                                                    .value
                                                            )
                                                        }
                                                    >
                                                        <option value="">
                                                            Select
                                                            disposition
                                                        </option>

                                                        {dispositions.map(
                                                            (
                                                                item,
                                                                index
                                                            ) => (
                                                                <option
                                                                    key={
                                                                        index
                                                                    }
                                                                    value={
                                                                        item.disposition
                                                                    }
                                                                >
                                                                    {
                                                                        item.disposition
                                                                    }
                                                                </option>
                                                            )
                                                        )}
                                                    </select>

                                                    <textarea
                                                        value={remarks}
                                                        onChange={(
                                                            event
                                                        ) =>
                                                            setRemarks(
                                                                event
                                                                    .target
                                                                    .value
                                                            )
                                                        }
                                                        placeholder="Add call remarks..."
                                                        rows={3}
                                                    />

                                                    <button
                                                        className="san-save-disposition"
                                                        onClick={
                                                            submitDisposition
                                                        }
                                                    >
                                                        Save Disposition
                                                    </button>
                                                </div>
                                            )}
                                    </div>
                                )}

                            {/* ============================================
                  HISTORY
              ============================================ */}

                            {activeTab ===
                                "history" && (
                                    <div className="san-history">
                                        {callHistory.length ===
                                            0 && (
                                                <div className="san-history-empty">
                                                    <Icon
                                                        name="history"
                                                        size={30}
                                                    />

                                                    <span>
                                                        No calls yet
                                                    </span>
                                                </div>
                                            )}

                                        {callHistory.map(
                                            (call) => (
                                                <div
                                                    className="san-history-item"
                                                    key={call.id}
                                                >
                                                    <div
                                                        className={`san-history-icon ${call.type ===
                                                            "Incoming"
                                                            ? "incoming"
                                                            : "outgoing"
                                                            }`}
                                                    >
                                                        <Icon
                                                            name={
                                                                call.type ===
                                                                    "Incoming"
                                                                    ? "phoneIncoming"
                                                                    : "phoneOutgoing"
                                                            }
                                                            size={16}
                                                        />
                                                    </div>

                                                    <div className="san-history-content">
                                                        <div className="san-history-phone">
                                                            {
                                                                call.phone
                                                            }
                                                        </div>

                                                        <div className="san-history-meta">
                                                            {
                                                                call.type
                                                            }{" "}
                                                            •{" "}
                                                            {call.duration
                                                                ? `${call.duration}s`
                                                                : "—"}
                                                        </div>
                                                    </div>

                                                    {call.recording && (
                                                        <div
                                                            className="san-recording-badge"
                                                            title={
                                                                call.recording
                                                            }
                                                        >
                                                            REC
                                                        </div>
                                                    )}

                                                    <button
                                                        className="san-history-call"
                                                        onClick={() =>
                                                            dial(
                                                                call.phone
                                                            )
                                                        }
                                                    >
                                                        <Icon
                                                            name="phone"
                                                            size={16}
                                                        />
                                                    </button>
                                                </div>
                                            )
                                        )}
                                    </div>
                                )}
                        </>
                    )}
                </div>

                {/* ================================================
            SAN IFRAME
        ================================================= */}

                <iframe
                    ref={iframeRef}
                    title="SAN CTI"
                    src={SAN_URL}
                    className="san-dialer-hidden-iframe"
                    allow="microphone; camera"
                />
            </>
        );
    }
);

SanDialer.displayName =
    "SanDialer";

export default SanDialer;